import { useEffect, useRef, memo, useState } from 'react'
import {
  createChart,
  IChartApi,
  ISeriesApi,
  HistogramData,
  CrosshairMode,
  Time,
  MouseEventParams,
  HistogramSeries,
} from 'lightweight-charts'
import type { ScreenerCandle } from '@/types'

interface VolumeChartProps {
  candles: ScreenerCandle[]
  symbol: string
  exchange?: string
  onCrosshairMove?: (time: Time | null, volume: number | null) => void
  syncedTime?: Time | null
  height?: number
}

interface TooltipData {
  time: string
  volume: number
  direction: 'up' | 'down'
}

function formatVolume(value: number): string {
  if (value >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(2) + 'B'
  }
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(2) + 'M'
  }
  if (value >= 1_000) {
    return (value / 1_000).toFixed(2) + 'K'
  }
  return value.toFixed(2)
}

function VolumeChartComponent({
  candles,
  symbol,
  exchange,
  onCrosshairMove,
  syncedTime,
  height = 150,
}: VolumeChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [hoveredData, setHoveredData] = useState<TooltipData | null>(null)

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current) return

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: '#1a1a2e' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2B2B43' },
        horzLines: { color: '#2B2B43' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          width: 1,
          color: '#758696',
          style: 3,
        },
        horzLine: {
          width: 1,
          color: '#758696',
          style: 3,
        },
      },
      rightPriceScale: {
        borderColor: '#2B2B43',
        scaleMargins: {
          top: 0.1,
          bottom: 0,
        },
      },
      timeScale: {
        borderColor: '#2B2B43',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: {
        vertTouchDrag: false,
      },
    })

    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: '#26a69a',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '',
    })

    chart.priceScale('').applyOptions({
      scaleMargins: {
        top: 0.1,
        bottom: 0,
      },
    })

    chartRef.current = chart
    seriesRef.current = volumeSeries

    // Subscribe to crosshair move
    chart.subscribeCrosshairMove((param: MouseEventParams) => {
      if (param.time && param.point) {
        const volumeData = param.seriesData.get(volumeSeries)
        if (volumeData && 'value' in volumeData) {
          const volume = (volumeData as HistogramData).value
          const color = (volumeData as HistogramData).color
          const direction = color?.includes('154') ? 'up' : 'down' // Check if green
          
          // Update tooltip
          if (tooltipRef.current) {
            const dateStr = new Date((param.time as number) * 1000).toLocaleString()
            setHoveredData({
              time: dateStr,
              volume,
              direction,
            })
            
            const coordinate = param.point
            tooltipRef.current.style.left = `${coordinate.x + 15}px`
            tooltipRef.current.style.top = `${coordinate.y + 15}px`
            tooltipRef.current.style.display = 'block'
          }
          
          if (onCrosshairMove) {
            onCrosshairMove(param.time, volume)
          }
        } else {
          if (tooltipRef.current) {
            tooltipRef.current.style.display = 'none'
          }
          if (onCrosshairMove) {
            onCrosshairMove(param.time, null)
          }
        }
      } else {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none'
        }
        setHoveredData(null)
        if (onCrosshairMove) {
          onCrosshairMove(null, null)
        }
      }
    })

    // Handle resize
    const handleResize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
        })
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
    }
  }, [onCrosshairMove])

  // Update data
  useEffect(() => {
    if (!seriesRef.current) return
    if (candles.length === 0) {
      seriesRef.current.setData([])
      return
    }

    const volumeData: HistogramData[] = candles.map((c, i) => {
      const prevClose = i > 0 ? candles[i - 1].close : c.open
      const isUp = c.close >= prevClose
      return {
        time: c.timestamp as Time,
        value: c.volume,
        color: isUp ? 'rgba(38, 166, 154, 0.7)' : 'rgba(239, 83, 80, 0.7)',
      }
    })

    seriesRef.current.setData(volumeData)
    chartRef.current?.timeScale().fitContent()
  }, [candles])

  // Sync crosshair from other charts
  useEffect(() => {
    if (!chartRef.current || syncedTime === undefined) return

    if (syncedTime === null) {
      chartRef.current.clearCrosshairPosition()
    } else {
      const lastCandle = candles[candles.length - 1]
      if (lastCandle && seriesRef.current) {
        chartRef.current.setCrosshairPosition(lastCandle.volume, syncedTime, seriesRef.current)
      }
    }
  }, [syncedTime, candles])

  // Get current volume for display
  const currentVolume = candles.length > 0 ? candles[candles.length - 1].volume : null

  return (
    <div className="relative">
      {/* Header */}
      <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-2">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
          Volume {exchange && <span className="text-xs text-gray-500">({exchange})</span>}
        </span>
        {currentVolume !== null && (
          <span className="text-sm font-semibold text-cyan-400">
            ${formatVolume(currentVolume)}
          </span>
        )}
      </div>
      
      {/* Chart container */}
      <div ref={containerRef} style={{ height }} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute hidden pointer-events-none bg-gray-900/95 border border-gray-700 rounded px-3 py-2 text-xs z-30"
        style={{ display: 'none' }}
      >
        {hoveredData && (
          <div className="space-y-1">
            <div className="text-gray-400">{hoveredData.time}</div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Volume:</span>
              <span 
                className="font-medium"
                style={{ color: hoveredData.direction === 'up' ? '#26a69a' : '#ef5350' }}
              >
                ${formatVolume(hoveredData.volume)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">Direction:</span>
              <span 
                className="font-medium"
                style={{ color: hoveredData.direction === 'up' ? '#26a69a' : '#ef5350' }}
              >
                {hoveredData.direction === 'up' ? '↑ Bullish' : '↓ Bearish'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const VolumeChart = memo(VolumeChartComponent)
